/**
 * Unsplash API integration
 * Documentation: https://unsplash.com/developers
 * 
 * Note: For production, register an app at https://unsplash.com/developers
 * and set REACT_APP_UNSPLASH_ACCESS_KEY in your .env file.
 */

const UNSPLASH_ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
const UNSPLASH_API_URL = 'https://api.unsplash.com';

/**
 * Get a placeholder image using a working image service
 * @param {string} query - Search query (used to generate consistent image)
 * @returns {string} Image URL
 */
function getPlaceholderImage(query) {
  // Use Picsum Photos with a seed based on the query for consistency
  // This provides working placeholder images until an Unsplash API key is configured
  const seed = query.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return `https://picsum.photos/seed/${seed}/800/600`;
}

/**
 * Search for photos on Unsplash
 * @param {string} query - Search query
 * @param {number} perPage - Number of results (default: 1)
 * @returns {Promise<string>} Image URL
 * @deprecated Use getUnsplashPhotoObject instead to get full photo object for compliance
 */
export async function getUnsplashImage(query, perPage = 1, resultIndex = 0) {
  try {
    // If no access key, use placeholder service
    if (!UNSPLASH_ACCESS_KEY) {
      console.warn('Unsplash API key not set. Using placeholder images. Register at https://unsplash.com/developers');
      return getPlaceholderImage(query);
    }

    // Use full Unsplash API with access key
    const response = await fetch(
      `${UNSPLASH_API_URL}/search/photos?query=${encodeURIComponent(query)}&per_page=${perPage}&orientation=landscape`,
      {
        headers: {
          'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Unsplash API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.results && data.results.length > 0) {
      // Return the specified result (default: first result, index 0)
      // You can specify a different index to get a different image from the search results
      const index = Math.min(resultIndex, data.results.length - 1);
      return data.results[index].urls.regular;
    }

    // Fallback to placeholder if no results
    return getPlaceholderImage(query);
  } catch (error) {
    console.error('Error fetching Unsplash image:', error);
    // Fallback to placeholder service
    return getPlaceholderImage(query);
  }
}

/**
 * Search for photos on Unsplash and return full photo object (for compliance)
 * @param {string} query - Search query
 * @param {number} perPage - Number of results (default: 10)
 * @param {number} resultIndex - Which result to return (default: 0)
 * @returns {Promise<Object|null>} Full Unsplash photo object or null
 */
export async function getUnsplashPhotoObject(query, perPage = 10, resultIndex = 0) {
  try {
    // If no access key, return null (will need placeholder handling)
    if (!UNSPLASH_ACCESS_KEY) {
      console.warn('Unsplash API key not set. Cannot fetch photo objects.');
      return null;
    }

    // Use full Unsplash API with access key
    const response = await fetch(
      `${UNSPLASH_API_URL}/search/photos?query=${encodeURIComponent(query)}&per_page=${perPage}&orientation=landscape`,
      {
        headers: {
          'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Unsplash API error: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.results && data.results.length > 0) {
      const index = Math.min(resultIndex, data.results.length - 1);
      return data.results[index]; // Return full photo object
    }

    return null;
  } catch (error) {
    console.error('Error fetching Unsplash photo object:', error);
    return null;
  }
}

/**
 * Get photo object by ID
 * @param {string} photoId - Unsplash photo ID
 * @returns {Promise<Object|null>} Full Unsplash photo object or null
 */
export async function getUnsplashPhotoById(photoId) {
  try {
    if (!UNSPLASH_ACCESS_KEY) {
      console.warn('Unsplash API key not set.');
      return null;
    }

    const response = await fetch(
      `${UNSPLASH_API_URL}/photos/${photoId}`,
      {
        headers: {
          'Authorization': `Client-ID ${UNSPLASH_ACCESS_KEY}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Unsplash API error: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching Unsplash photo by ID:', error);
    return null;
  }
}

/**
 * Get search query for an item
 * @param {Object} item - Content item (recipe, activity, or story)
 * @param {string} category - Category name (recipes, activities, stories)
 * @returns {string} Search query for Unsplash
 */
function getOptimizedQuery(item, category) {
  // First, check if the item has an imageSearchQuery field (preferred)
  if (item?.imageSearchQuery) {
    return item.imageSearchQuery;
  }

  // Fallback: use the item title directly, or category-specific default
  if (item?.title) {
    return item.title;
  }

  const defaults = {
    recipes: 'jewish food traditional',
    activities: 'jewish family activity',
    stories: 'jewish story',
  };

  return defaults[category] || 'jewish culture';
}

/**
 * Get a random image for a category
 * @param {string} category - Category name (recipes, activities, stories)
 * @param {Object|string} itemOrTitle - Content item object (preferred) or item title (fallback)
 * @param {number} resultIndex - Which result to use from search (0 = first, 1 = second, etc.)
 * @returns {Promise<string>} Image URL
 * @deprecated Use getCategoryPhotoObject instead to get full photo object for compliance
 */
export async function getCategoryImage(category, itemOrTitle = '', resultIndex = 0) {
  // Handle both object and string for backward compatibility
  const item = typeof itemOrTitle === 'object' ? itemOrTitle : { title: itemOrTitle };
  
  // Get optimized search query
  const query = getOptimizedQuery(item, category);
  
  // If no API key, use placeholder with category-specific seeds
  if (!UNSPLASH_ACCESS_KEY) {
    // For placeholder, use a seed based on the optimized query for consistency
    const seed = query.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `https://picsum.photos/seed/${seed}/800/600`;
  }

  // Fetch multiple results so we can pick a specific one
  return getUnsplashImage(query, 10, resultIndex);
}

/**
 * Get a photo object for a category (for compliance - returns full photo object)
 * @param {string} category - Category name (recipes, activities, stories)
 * @param {Object|string} itemOrTitle - Content item object (preferred) or item title (fallback)
 * @param {number} resultIndex - Which result to use from search (0 = first, 1 = second, etc.)
 * @returns {Promise<Object|null>} Full Unsplash photo object or null
 */
export async function getCategoryPhotoObject(category, itemOrTitle = '', resultIndex = 0) {
  // Handle both object and string for backward compatibility
  const item = typeof itemOrTitle === 'object' ? itemOrTitle : { title: itemOrTitle };
  
  // Get optimized search query
  const query = getOptimizedQuery(item, category);
  
  // If no API key, return null
  if (!UNSPLASH_ACCESS_KEY) {
    return null;
  }

  // Fetch full photo object
  return getUnsplashPhotoObject(query, 10, resultIndex);
}

/**
 * Pre-fetch images for content items
 * This can be called to cache images before displaying
 */
export async function prefetchContentImages(contentItems, category) {
  const imagePromises = contentItems.map(async (item) => {
    const imageUrl = await getCategoryImage(category, item);
    return { ...item, imageUrl };
  });
  
  return Promise.all(imagePromises);
}

